/**
 * Feature-flag consumption hooks.
 *
 * Loads all resolved flags for the current user once per session and keeps them live
 * via the WebSocket gateway — no logout/reload required to pick up a changed flag.
 *
 * IMPORTANT: only exact, concrete WS topic strings are used here
 * (`entity:feature-flags:global`, `entity:feature-flags:user:{userId}`). pro-api's WS
 * gateway does a literal Redis SUBSCRIBE on whatever topic string the browser sends —
 * it never does a glob-pattern PSUBSCRIBE — so a wildcard topic like `entity:products:*`
 * used elsewhere in this app never actually matches a concrete published channel. Do not
 * copy that pattern here.
 */
import * as React from 'react';
import { useAtom, useSetAtom } from 'jotai';
import { featureFlagsAtom, featureFlagsLoadedAtom } from './atoms';
import { fetchResolvedFeatureFlags } from '../api/feature-flags';
import { useAuth } from '../auth/hooks';
import { useWebSocket } from '../useWebSocket';

/** Mount once near the app root (e.g. AuthenticatedLayout) — not per-consumer. */
export function useFeatureFlagsBootstrap() {
  const { accessToken, user, isAuthenticated } = useAuth();
  const setFlags = useSetAtom(featureFlagsAtom);
  const setLoaded = useSetAtom(featureFlagsLoadedAtom);
  const { subscribe, unsubscribe } = useWebSocket();
  const orgIdsKey = (user?.organizations ?? [])
    .map((o) => o.id)
    .sort()
    .join(',');

  const load = React.useCallback(async () => {
    if (!accessToken) return;
    try {
      const resolved = await fetchResolvedFeatureFlags(accessToken);
      setFlags(new Map(resolved.map((f) => [f.key, f])));
    } finally {
      setLoaded(true);
    }
  }, [accessToken, setFlags, setLoaded]);

  React.useEffect(() => {
    if (!isAuthenticated) return;
    // Defer past first paint so certifications / route data win the network.
    let idleId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const run = () => {
      void load();
    };
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(run, { timeout: 1500 });
    } else {
      timeoutId = setTimeout(run, 0);
    }
    return () => {
      if (idleId != null && typeof window !== 'undefined' && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId != null) clearTimeout(timeoutId);
    };
  }, [isAuthenticated, load, orgIdsKey]);

  React.useEffect(() => {
    if (!isAuthenticated) return;

    const handler = () => void load();
    const topics = [
      'entity:feature-flags:global',
      ...(user?.id ? [`entity:feature-flags:user:${user.id}`] : []),
      ...(orgIdsKey ? orgIdsKey.split(',').map((id) => `entity:feature-flags:org:${id}`) : []),
    ];
    subscribe(topics, handler);

    return () => {
      unsubscribe(topics, handler);
    };
  }, [isAuthenticated, user?.id, orgIdsKey, subscribe, unsubscribe, load]);
}

/** Read one resolved flag's raw (string) value + whether flags have loaded yet. */
export function useFeatureFlagRaw(key: string): { value: string | undefined; loaded: boolean } {
  const [flags] = useAtom(featureFlagsAtom);
  const [loaded] = useAtom(featureFlagsLoadedAtom);
  return { value: flags.get(key)?.value, loaded };
}

/** Convenience for boolean flags — parses "true"/"1" as true. */
export function useBooleanFeatureFlag(key: string, defaultValue = false): boolean {
  const { value, loaded } = useFeatureFlagRaw(key);
  if (!loaded || value === undefined) return defaultValue;
  return value === 'true' || value === '1';
}

/** Convenience for numeric flags. Invalid / missing → defaultValue. */
export function useNumberFeatureFlag(key: string, defaultValue = 0): number {
  const { value, loaded } = useFeatureFlagRaw(key);
  if (!loaded || value === undefined) return defaultValue;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : defaultValue;
}
